import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Modal from '../../components/ui/Modal';
import Select from '../../components/ui/Select';
import { useToast } from '../../components/context/ToastContext';
import repositoryContainer from '../../services/RepositoryContainer';
import InviteStaffModal from './components/InviteStaffModal';
import { useAuth } from '../../components/context/AuthContext';

const ROLE_OPTIONS = [
  { value: 'Staff', label: 'Staff' },
  { value: 'Admin', label: 'Admin' },
];

const getApiErrorMessage = (error, fallback) => {
  if (error?.data?.message) return error.data.message;
  if (typeof error?.data === 'string' && error.data.trim()) return error.data;

  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return fallback;
};

const getRole = (member) => (
  member?.roles?.some((role) => String(role).toLowerCase() === 'admin') ? 'Admin' : 'Staff'
);

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StatusBadge = ({ enabled }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-caption font-medium ${
    enabled ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
  }`}>
    {enabled ? 'Active' : 'Disabled'}
  </span>
);

const PendingBadge = () => (
  <span className="inline-flex rounded-full bg-warning/10 px-2.5 py-1 text-xs text-warning font-caption font-medium">
    Pending
  </span>
);

const EmptyState = ({ icon, title, description }) => (
  <div className="px-5 py-12 text-center">
    <Icon name={icon} size={42} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-60" />
    <h3 className="font-heading font-semibold text-lg text-foreground">{title}</h3>
    <p className="mt-1 text-sm text-muted-foreground font-caption">{description}</p>
  </div>
);

const StaffManagement = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [resendingInvitationId, setResendingInvitationId] = useState(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [memberToToggle, setMemberToToggle] = useState(null);
  const [isChangingAccess, setIsChangingAccess] = useState(false);

  const loadTeam = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const [staffResponse, invitationsResponse] = await Promise.all([
        repositoryContainer.staff.list(),
        repositoryContainer.staff.listPendingInvitations(),
      ]);
      setMembers(Array.isArray(staffResponse) ? staffResponse : []);
      setInvitations(Array.isArray(invitationsResponse) ? invitationsResponse : []);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Team information could not be loaded.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const activeCount = useMemo(
    () => members.filter((member) => member?.isEnabled).length,
    [members]
  );
  const adminCount = useMemo(
    () => members.filter((member) => getRole(member) === 'Admin').length,
    [members]
  );
  const enabledAdminCount = useMemo(
    () => members.filter((member) => member?.isEnabled && getRole(member) === 'Admin').length,
    [members]
  );

  const handleInvite = async (payload) => {
    setIsInviting(true);
    try {
      await repositoryContainer.staff.invite(payload);
      addToast(`Invitation sent to ${payload.email}.`, 'success');
      setIsInviteOpen(false);
      await loadTeam();
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The invitation could not be sent.'), 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (member, role) => {
    if (role === getRole(member)) return;
    setUpdatingMemberId(member.id);
    try {
      await repositoryContainer.staff.updateRole(member.id, role);
      setMembers((current) => current.map((item) => (
        item.id === member.id ? { ...item, roles: [role] } : item
      )));
      addToast(`${member.firstName}'s role was updated to ${role}.`, 'success');
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The role could not be updated.'), 'error');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleResend = async (invitation) => {
    setResendingInvitationId(invitation.invitationId);
    try {
      await repositoryContainer.staff.resendInvitation(invitation.invitationId);
      addToast(`A new invitation was sent to ${invitation.email}.`, 'success');
      await loadTeam();
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The invitation could not be resent.'), 'error');
    } finally {
      setResendingInvitationId(null);
    }
  };

  const handleRevoke = async () => {
    if (!invitationToRevoke) return;
    setIsRevoking(true);
    try {
      await repositoryContainer.staff.revokeInvitation(invitationToRevoke.invitationId);
      addToast(`Invitation for ${invitationToRevoke.email} was revoked.`, 'success');
      setInvitationToRevoke(null);
      await loadTeam();
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The invitation could not be revoked.'), 'error');
    } finally {
      setIsRevoking(false);
    }
  };

  const handleAccessChange = async () => {
    if (!memberToToggle) return;
    setIsChangingAccess(true);
    try {
      const nextEnabled = !memberToToggle.isEnabled;
      const result = nextEnabled
        ? await repositoryContainer.staff.enable(memberToToggle.id)
        : await repositoryContainer.staff.disable(memberToToggle.id);
      setMembers((current) => current.map((member) => (
        member.id === memberToToggle.id
          ? { ...member, isEnabled: result?.isEnabled ?? nextEnabled }
          : member
      )));
      addToast(`${memberToToggle.firstName}'s access was ${nextEnabled ? 'enabled' : 'disabled'}.`, 'success');
      setMemberToToggle(null);
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The member access could not be changed.'), 'error');
    } finally {
      setIsChangingAccess(false);
    }
  };

  const currentUserId = user?.id || user?.userId;
  const cannotDisable = (member) => member.isEnabled && (
    member.id === currentUserId || (getRole(member) === 'Admin' && enabledAdminCount <= 1)
  );

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
      />

      <div className={`transition-all duration-250 ease-elegant ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation isSidebarCollapsed={isSidebarCollapsed} />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                  Team Members
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Invite staff and control who can manage this workspace.
                </p>
              </div>
              <Button iconName="UserPlus" onClick={() => setIsInviteOpen(true)}>
                Invite Team Member
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground font-caption">Active members</p>
                <p className="mt-1 font-heading text-xl sm:text-2xl font-bold text-foreground">{activeCount}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground font-caption">Administrators</p>
                <p className="mt-1 font-heading text-xl sm:text-2xl font-bold text-foreground">{adminCount}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-muted-foreground font-caption">Pending invitations</p>
                <p className="mt-1 font-heading text-xl sm:text-2xl font-bold text-foreground">{invitations.length}</p>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-4" aria-label="Loading team members">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-20 animate-pulse rounded-xl border border-border bg-card" />
                ))}
              </div>
            ) : loadError ? (
              <div className="rounded-xl border border-error/20 bg-error/5 p-6 text-center">
                <Icon name="AlertCircle" size={30} color="var(--color-error)" className="mx-auto mb-2" />
                <p className="text-sm text-error font-caption">{loadError}</p>
                <Button variant="outline" size="sm" className="mt-4" iconName="RefreshCw" onClick={loadTeam}>
                  Try Again
                </Button>
              </div>
            ) : (
              <>
                <section className="overflow-visible rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border px-4 py-4 md:px-6">
                    <div>
                      <h2 className="font-heading text-lg font-semibold text-foreground">Team Members</h2>
                      <p className="mt-1 text-sm text-muted-foreground font-caption">Accepted users with access to the workspace.</p>
                    </div>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground font-caption font-medium">
                      {members.length}
                    </span>
                  </div>

                  {members.length === 0 ? (
                    <EmptyState icon="Users" title="No team members yet" description="Invite your first team member to get started." />
                  ) : (
                    <>
                      <div className="hidden md:block">
                        <table className="w-full">
                          <thead className="border-b border-border bg-muted/30">
                            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground font-caption">
                              <th className="px-6 py-3 font-medium">Member</th>
                              <th className="px-6 py-3 font-medium">Email</th>
                              <th className="px-6 py-3 font-medium">Role</th>
                              <th className="px-6 py-3 font-medium">Status</th>
                              <th className="px-6 py-3 text-right font-medium">Access</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {members.map((member) => (
                              <tr key={member.id} className="transition-colors hover:bg-muted/20">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm text-primary font-caption font-semibold">
                                      {(member.firstName?.[0] || member.email?.[0] || '?').toUpperCase()}
                                    </div>
                                    <span className="text-sm text-foreground font-caption font-semibold">
                                      {member.firstName} {member.lastName}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground font-caption">{member.email}</td>
                                <td className="w-44 px-6 py-4">
                                  <Select
                                    value={getRole(member)}
                                    options={ROLE_OPTIONS}
                                    onChange={(role) => handleRoleChange(member, role)}
                                    disabled={updatingMemberId === member.id || !member.isEnabled}
                                    loading={updatingMemberId === member.id}
                                    aria-label={`Role for ${member.firstName} ${member.lastName}`}
                                  />
                                </td>
                                <td className="px-6 py-4"><StatusBadge enabled={member.isEnabled} /></td>
                                <td className="px-6 py-4 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={member.isEnabled ? 'text-error hover:bg-error/10 hover:text-error' : 'text-success hover:bg-success/10 hover:text-success'}
                                    onClick={() => setMemberToToggle(member)}
                                    disabled={cannotDisable(member)}
                                  >
                                    {member.isEnabled ? 'Disable' : 'Enable'}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="divide-y divide-border md:hidden">
                        {members.map((member) => (
                          <article key={member.id} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-caption font-semibold text-foreground text-base mb-1">
                                  {member.firstName} {member.lastName}
                                </p>
                                <p className="truncate text-sm text-muted-foreground font-caption">{member.email}</p>
                              </div>
                              <StatusBadge enabled={member.isEnabled} />
                            </div>
                            <div className="mt-4">
                              <Select
                                label="Role"
                                value={getRole(member)}
                                options={ROLE_OPTIONS}
                                onChange={(role) => handleRoleChange(member, role)}
                                disabled={updatingMemberId === member.id || !member.isEnabled}
                                loading={updatingMemberId === member.id}
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              className={`mt-3 ${member.isEnabled ? 'text-error' : 'text-success'}`}
                              onClick={() => setMemberToToggle(member)}
                              disabled={cannotDisable(member)}
                            >
                              {member.isEnabled ? 'Disable Access' : 'Enable Access'}
                            </Button>
                          </article>
                        ))}
                      </div>
                    </>
                  )}
                </section>

                <section className="overflow-hidden rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border px-4 py-4 md:px-6">
                    <div>
                      <h2 className="font-heading text-lg font-semibold text-foreground">Pending Invitations</h2>
                      <p className="mt-1 text-sm text-muted-foreground font-caption">Invitations that have not been accepted yet.</p>
                    </div>
                    <span className="rounded-full bg-warning/10 px-2.5 py-1 text-xs text-warning font-caption font-medium">
                      {invitations.length}
                    </span>
                  </div>

                  {invitations.length === 0 ? (
                    <EmptyState icon="MailCheck" title="No pending invitations" description="All sent invitations have been handled." />
                  ) : (
                    <>
                      <div className="hidden overflow-x-auto md:block">
                        <table className="w-full">
                          <thead className="border-b border-border bg-muted/30">
                            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground font-caption">
                              <th className="px-6 py-3 font-medium">Invitee</th>
                              <th className="px-6 py-3 font-medium">Role</th>
                              <th className="px-6 py-3 font-medium">Invited by</th>
                              <th className="px-6 py-3 font-medium">Expires</th>
                              <th className="px-6 py-3 text-right font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {invitations.map((invitation) => (
                              <tr key={invitation.invitationId} className="transition-colors hover:bg-muted/20">
                                <td className="px-6 py-4">
                                  <p className="text-sm text-foreground font-caption font-semibold">
                                    {invitation.firstName} {invitation.lastName}
                                  </p>
                                  <p className="mt-1 text-sm text-muted-foreground font-caption">{invitation.email}</p>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-foreground font-caption">{invitation.role}</span>
                                    <PendingBadge />
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground font-caption">
                                  {invitation.invitedByName || 'Administrator'}
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground font-caption">
                                  {formatDate(invitation.expiresAt)}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="Send"
                                      loading={resendingInvitationId === invitation.invitationId}
                                      onClick={() => handleResend(invitation)}
                                    >
                                      Resend
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-error hover:bg-error/10 hover:text-error"
                                      onClick={() => setInvitationToRevoke(invitation)}
                                    >
                                      Revoke
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="divide-y divide-border md:hidden">
                        {invitations.map((invitation) => (
                          <article key={invitation.invitationId} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-caption font-semibold text-foreground text-base mb-1">
                                  {invitation.firstName} {invitation.lastName}
                                </p>
                                <p className="truncate text-sm text-muted-foreground font-caption">{invitation.email}</p>
                              </div>
                              <PendingBadge />
                            </div>
                            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm font-caption">
                              <div>
                                <dt className="text-muted-foreground">Role</dt>
                                <dd className="mt-1 text-foreground">{invitation.role}</dd>
                              </div>
                              <div>
                                <dt className="text-muted-foreground">Expires</dt>
                                <dd className="mt-1 text-foreground">{formatDate(invitation.expiresAt)}</dd>
                              </div>
                            </dl>
                            <div className="mt-4 flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                loading={resendingInvitationId === invitation.invitationId}
                                onClick={() => handleResend(invitation)}
                              >
                                Resend
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 text-error hover:bg-error/10 hover:text-error"
                                onClick={() => setInvitationToRevoke(invitation)}
                              >
                                Revoke
                              </Button>
                            </div>
                          </article>
                        ))}
                      </div>
                    </>
                  )}
                </section>
              </>
            )}
          </div>
        </main>
      </div>

      <InviteStaffModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSubmit={handleInvite}
        isSubmitting={isInviting}
      />

      <Modal
        isOpen={Boolean(invitationToRevoke)}
        onClose={() => !isRevoking && setInvitationToRevoke(null)}
        title="Revoke Invitation"
      >
        <p className="text-sm text-muted-foreground font-caption">
          Revoke the invitation sent to <span className="text-foreground font-medium">{invitationToRevoke?.email}</span>?
          Their current invitation link will no longer work.
        </p>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button variant="outline" onClick={() => setInvitationToRevoke(null)} disabled={isRevoking}>
            Keep Invitation
          </Button>
          <Button variant="danger" loading={isRevoking} onClick={handleRevoke}>
            Revoke Invitation
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(memberToToggle)}
        onClose={() => !isChangingAccess && setMemberToToggle(null)}
        title={memberToToggle?.isEnabled ? 'Disable Team Member' : 'Enable Team Member'}
      >
        <p className="text-sm text-muted-foreground font-caption">
          {memberToToggle?.isEnabled
            ? `Disable access for ${memberToToggle?.firstName} ${memberToToggle?.lastName}? They will be signed out and unable to use the workspace.`
            : `Restore workspace access for ${memberToToggle?.firstName} ${memberToToggle?.lastName}?`}
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => setMemberToToggle(null)} disabled={isChangingAccess}>Cancel</Button>
          <Button
            variant={memberToToggle?.isEnabled ? 'danger' : 'default'}
            loading={isChangingAccess}
            onClick={handleAccessChange}
          >
            {memberToToggle?.isEnabled ? 'Disable Access' : 'Enable Access'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default StaffManagement;
